import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { siteInfo, navLinks } from '@/mocks/site';
import usePageMeta from '@/hooks/usePageMeta';
import JsonLd from '@/components/feature/JsonLd';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import Breadcrumbs from '@/components/feature/Breadcrumbs';

const FORM_ID = 'dah9163p14a1h3mr6fng';
const SUBMIT_URL = 'https://readdy.ai/api/form/dah9163p14a1h3mr6fng';

type Status = 'idle' | 'submitting' | 'success' | 'error';

const ContactPage = () => {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');
  const [topic, setTopic] = useState('提供线索');
  const [messageLength, setMessageLength] = useState(0);

  usePageMeta({
    title: '联系我们 - 投稿爆料的正确方式 - 爆瓜爆料',
    description:
      '联系爆瓜爆料：提供线索、内容反馈、合作咨询都可以在这里留言。投稿身份全程保密，线索在核实前不会对外发布，工作日 48 小时内回复。',
    keywords: '联系爆瓜爆料,爆料投稿,线索提供,内容反馈',
    canonical: 'https://sibzb.cn/contact',
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError('');

    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      const text = String(value);
      if (text.trim() === '') return;
      payload.append(key, text);
    });

    setStatus('submitting');
    try {
      const response = await fetch(SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = /spam|form data is spam/i.test(serverMsg);
      const isOk = response.ok && parsed?.code === 'OK' && !isSpam;

      if (isOk) {
        setStatus('success');
        form.reset();
        setMessageLength(0);
        setTopic('提供线索');
      } else {
        setStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试，或直接发送邮件到投稿邮箱。');
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，留言未能送达。请检查网络后重试，或直接发送邮件到投稿邮箱。');
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'ContactPage',
              name: '联系爆瓜爆料',
              url: 'https://sibzb.cn/contact',
              description: '提供线索、内容反馈与合作咨询的留言页面。',
            },
            {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://sibzb.cn/' },
                { '@type': 'ListItem', position: 2, name: '联系我们', item: 'https://sibzb.cn/contact' },
              ],
            },
          ],
        }}
      />
      <SiteHeader />

      <main className="flex-1 px-4 md:px-6 py-6 md:py-8">
        <Breadcrumbs items={[{ label: '首页', to: '/' }, { label: '联系我们' }]} />

        <div className="mt-5 grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8">
          <section className="lg:col-span-3" aria-labelledby="contact-form-title">
            <h1 id="contact-form-title" className="font-heading text-3xl font-black text-foreground-950">
              给我们留言
            </h1>
            <p className="mt-3 text-sm leading-7 text-foreground-600">
              无论是提供线索、指出内容问题，还是洽谈合作，都可以在这里提交。带 <span className="text-accent-600">*</span>{' '}
              的是必填项，线索类留言请尽量写清时间、地点和你愿意被核实的方式。
            </p>

            {status === 'success' ? (
              <div className="mt-6 rounded-lg border border-primary-200 bg-primary-50 p-6 text-center">
                <span className="w-14 h-14 mx-auto rounded-full bg-primary-500 text-background-50 flex items-center justify-center mb-4">
                  <i className="ri-check-line text-2xl" />
                </span>
                <h2 className="font-heading text-lg font-bold text-foreground-950">留言已送达</h2>
                <p className="mt-2 text-sm leading-6 text-foreground-700">
                  感谢你的提醒。编辑团队会在工作日 48 小时内跟进，投稿身份全程保密。
                </p>
                <button
                  type="button"
                  onClick={() => setStatus('idle')}
                  className="mt-5 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-4 py-2 text-sm text-primary-800 transition-colors hover:bg-primary-100 cursor-pointer"
                >
                  <i className="ri-edit-line" />
                  再提交一条
                </button>
              </div>
            ) : (
              <form
                id={`form-${FORM_ID}`}
                data-readdy-form
                onSubmit={handleSubmit}
                className="mt-6 flex flex-col gap-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="contact-name" className="text-sm text-foreground-800">
                      你的称呼 <span className="text-accent-600">*</span>
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      maxLength={40}
                      placeholder="怎么称呼你，或用昵称"
                      className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="contact-email" className="text-sm text-foreground-800">
                      联系邮箱 <span className="text-accent-600">*</span>
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      placeholder="用于回复你的留言"
                      className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label htmlFor="contact-topic" className="text-sm text-foreground-800">
                    留言类型
                  </label>
                  <select
                    id="contact-topic"
                    name="topic"
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                    className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  >
                    <option value="提供线索">提供线索</option>
                    <option value="内容反馈">内容反馈</option>
                    <option value="投稿合作">投稿合作</option>
                    <option value="其他问题">其他问题</option>
                  </select>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label htmlFor="contact-message" className="text-sm text-foreground-800">
                    留言内容 <span className="text-accent-600">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    rows={6}
                    maxLength={500}
                    onChange={(e) => setMessageLength(e.target.value.length)}
                    placeholder="请描述你掌握的情况：事件时间、涉及方、你能提供的佐证方式等，最多 500 字。"
                    className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-6 text-foreground-900 outline-none transition-colors resize-y focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  />
                  <span className="self-end text-xs text-foreground-500">{messageLength}/500</span>
                </div>

                <div className="website_alt-field" aria-hidden="true">
                  <label htmlFor="contact-website-alt">网址（请勿填写）</label>
                  <input
                    id="contact-website-alt"
                    name="website_alt"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    readOnly
                  />
                </div>

                {status === 'error' && (
                  <div className="rounded-md border border-accent-300 bg-accent-50 px-4 py-3 text-sm leading-6 text-accent-800 flex items-start gap-2">
                    <i className="ri-error-warning-line mt-0.5" />
                    <span>{formError}</span>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <button
                    type="submit"
                    disabled={status === 'submitting'}
                    className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-600 px-6 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-700 disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
                  >
                    <i className={status === 'submitting' ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-line'} />
                    {status === 'submitting' ? '正在提交' : '提交留言'}
                  </button>
                  <span className="text-xs text-foreground-500">我们不会公开你的联系方式</span>
                </div>
              </form>
            )}
          </section>

          <aside className="lg:col-span-2 flex flex-col gap-4">
            <div className="rounded-lg bg-background-100 border border-background-200 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950 flex items-center gap-2">
                <i className="ri-mail-line text-primary-600" />
                直接发邮件
              </h2>
              <p className="mt-2 text-sm leading-6 text-foreground-600">
                如果留言不方便，也可以把材料整理后发送到投稿邮箱，标题注明"线索"即可。
              </p>
              <p className="mt-3 text-sm text-secondary-800 bg-secondary-100 rounded-md px-3 py-2 break-all">
                {siteInfo.email}
              </p>
            </div>

            <div className="rounded-lg bg-background-100 border border-background-200 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950 flex items-center gap-2">
                <i className="ri-shield-keyhole-line text-primary-600" />
                投稿须知
              </h2>
              <ul className="mt-3 space-y-3 text-sm leading-6 text-foreground-700">
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line text-primary-600 mt-0.5" />
                  投稿身份全程保密，不会出现在任何公开内容中。
                </li>
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line text-primary-600 mt-0.5" />
                  线索在完成核实前不会对外发布，也不承诺必然上线。
                </li>
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line text-primary-600 mt-0.5" />
                  请避免提交涉及他人隐私或未经证实的内容。
                </li>
              </ul>
            </div>

            <div className="rounded-lg border border-background-200 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">快速导航</h2>
              <div className="mt-3 grid grid-cols-2 gap-2">
                {navLinks.slice(0, 6).map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className="rounded-md bg-background-100 px-3 py-2 text-xs text-foreground-700 transition-colors hover:bg-primary-100 hover:text-primary-800"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
};

export default ContactPage;